





















































































































































































































import { Component, Vue } from 'vue-property-decorator'
import PageHead from '@/components/PageHead.vue'
import { Notify } from '@/helpers/Notify'
import { Api } from '@/fetch/Api'
import type { AiSeoData, AiSeoPrefill, AiSeoImage } from '@/interfaces'

@Component({
  components: { PageHead },
})
export default class AiSeoView extends Vue {
  pageTitle = 'AI SEO'
  saveInProgress = false
  deleteInProgress = false
  fieldErrors: Record<string, string> = {}
  headshotImages: AiSeoImage[] = []

  form = {
    full_name: '',
    practice_name: '',
    practitioner_type: '',
    practitioner_type_custom: '',
    website_url: '',
    phone: '',
    headshot_url: '',
    price_range_from: null as number | null,
    price_range_to: null as number | null,
    services: [''],
    online_therapy: false,
    specialisms: '',
    languages: '',
    profile_urls: [''],
    street_address: '',
    street_address_line_2: '',
    city: '',
    region: '',
    postcode: '',
    country: '',
    description: '',
  }

  mounted() {
    this.loadData()
    this.loadImages()
  }

  public async loadData() {
    this.saveInProgress = true
    try {
      const response = await Api.getAiSeo()
      this.saveInProgress = false
      if ((response as AiSeoPrefill).prefill) {
        const prefill = response as AiSeoPrefill
        this.form.full_name = prefill.full_name || ''
        this.form.website_url = prefill.website_url || ''
        this.form.phone = prefill.phone || ''
        this.form.headshot_url = prefill.headshot_url || ''
      } else {
        const data = response as AiSeoData
        this.form.full_name = data.full_name || ''
        this.form.practice_name = data.practice_name || ''
        const PREDEFINED_TYPES = ['psychotherapist', 'reflexologist', 'hypnotherapist', 'counsellor']
        const raw = data.practitioner_type || ''
        if (PREDEFINED_TYPES.includes(raw)) {
          this.form.practitioner_type = raw
          this.form.practitioner_type_custom = ''
        } else if (raw) {
          this.form.practitioner_type = 'other'
          this.form.practitioner_type_custom = raw
        } else {
          this.form.practitioner_type = ''
          this.form.practitioner_type_custom = ''
        }
        this.form.website_url = data.website_url || ''
        this.form.phone = data.phone || ''
        this.form.headshot_url = data.headshot_url || ''
        this.form.price_range_from = data.price_range_from ?? null
        this.form.price_range_to = data.price_range_to ?? null
        this.form.services = data.services && data.services.length ? data.services.map(s => s.name) : ['']
        this.form.online_therapy = data.online_therapy || false
        this.form.specialisms = data.specialisms && data.specialisms.length ? data.specialisms.join(', ') : ''
        this.form.languages = data.languages && data.languages.length ? data.languages.join(', ') : ''
        this.form.profile_urls = data.profile_urls && data.profile_urls.length ? data.profile_urls.map(p => p.url) : ['']
        this.form.street_address = data.street_address || ''
        this.form.street_address_line_2 = data.street_address_line_2 || ''
        this.form.city = data.city || ''
        this.form.region = data.region || ''
        this.form.postcode = data.postcode || ''
        this.form.country = data.country || ''
        this.form.description = data.description || ''
      }
    } catch (e) {
      this.saveInProgress = false
      Notify.create({ type: 'error', title: 'Something went wrong...', text: e })
    }
  }

  public async loadImages() {
    try {
      const response = await Api.getAiSeoImages()
      this.headshotImages = response.images
    } catch (e) {
      // non-critical — dropdown will just be empty
    }
  }

  public addService() {
    this.form.services.push('')
  }

  public removeService(index: number) {
    if (this.form.services.length === 1) {
      this.form.services[0] = ''
      this.form.services = [...this.form.services]
    } else {
      this.form.services.splice(index, 1)
    }
  }

  public addProfileUrl() {
    this.form.profile_urls.push('')
  }

  public removeProfileUrl(index: number) {
    if (this.form.profile_urls.length === 1) {
      this.form.profile_urls[0] = ''
      this.form.profile_urls = [...this.form.profile_urls]
    } else {
      this.form.profile_urls.splice(index, 1)
    }
  }

  public async submitForm() {
    this.saveInProgress = true
    this.fieldErrors = {}

    const payload = {
      full_name: this.form.full_name || null,
      practice_name: this.form.practice_name || null,
      practitioner_type: this.form.practitioner_type === 'other'
        ? (this.form.practitioner_type_custom.trim() || null)
        : (this.form.practitioner_type || null),
      website_url: this.form.website_url || null,
      phone: this.form.phone || null,
      headshot_url: this.form.headshot_url || null,
      price_range_from: this.form.price_range_from,
      price_range_to: this.form.price_range_to,
      services: this.form.services.filter(s => s.trim() !== ''),
      online_therapy: this.form.online_therapy,
      specialisms: this.form.specialisms || null,
      languages: this.form.languages || null,
      profile_urls: this.form.profile_urls.filter(u => u.trim() !== ''),
      street_address: this.form.street_address || null,
      street_address_line_2: this.form.street_address_line_2 || null,
      city: this.form.city || null,
      region: this.form.region || null,
      postcode: this.form.postcode || null,
      country: this.form.country || null,
      description: this.form.description || null,
    }

    try {
      const result = await Api.saveAiSeo(payload)
      this.saveInProgress = false

      if (result.errors) {
        const firstErrors: Record<string, string> = {}
        for (const field in result.errors) {
          if (result.errors[field] && result.errors[field].length) {
            firstErrors[field] = result.errors[field][0]
          }
        }
        this.fieldErrors = firstErrors
        Notify.create({ type: 'error', title: 'Validation Error', text: result.message || 'Please correct the errors below.' })
      } else {
        Notify.create({ type: 'success', title: 'Saved!', text: 'AI SEO data saved successfully.' })
        await this.loadData()
      }
    } catch (e) {
      this.saveInProgress = false
      Notify.create({ type: 'error', title: 'Something went wrong...', text: e })
    }
  }

  public confirmDelete() {
    const h = this.$createElement
    this.$bvModal.msgBoxConfirm(
      [h('span', { domProps: { innerHTML: 'Are you sure you want to delete all AI SEO data? This cannot be undone.' } })],
      {
        title: [h('span', { domProps: { innerHTML: '<b>Delete Confirmation</b>' } })],
        size: 'md',
        buttonSize: 'sm',
        okVariant: 'danger',
        cancelVariant: 'secondary',
        okTitle: 'Delete',
        cancelTitle: 'Cancel',
        footerClass: 'p-2',
        hideHeaderClose: false,
        centered: true,
      },
    ).then(async (confirmed) => {
      if (confirmed) {
        await this.deleteData()
      }
    }).catch(() => { /* dismissed */ })
  }

  public async deleteData() {
    this.deleteInProgress = true
    try {
      await Api.deleteAiSeo()
      this.deleteInProgress = false
      Notify.create({ type: 'success', title: 'Deleted!', text: 'AI SEO data has been removed successfully.' })
      await this.loadData()
    } catch (e) {
      this.deleteInProgress = false
      Notify.create({ type: 'error', title: 'Something went wrong...', text: e })
    }
  }
}
